import React, { useState, useEffect, useRef } from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import { withRouter } from "react-router";
import SpringApi from "api/SpringApi";
import Loader from "components/common/Loader";
import Toaster from "components/common/Toaster";
import Button from "components/button/Button";

const BASE = "/api/v1/integrations/eposnow";

const EMPTY_FORM = {
  apiKey: "",
  eposStoreId: "",
  eposTerminalId: "",
  tillId: "",
  eposAccessToken: "",
  eposRefreshToken: ""
};

const EposNowTable = props => {
  const [tblData, setTblData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const toaster = useRef();

  useEffect(() => {
    fetchMappings();
    fetchWebhookInfo();
  }, []);

  const fetchMappings = () => {
    setLoader(true);
    SpringApi.get(`${BASE}/mappings`)
      .then(res => {
        setTblData(res.data.data || []);
        setLoader(false);
      })
      .catch(err => {
        if (toaster.current) toaster.current.error(err.message);
        setLoader(false);
      });
  };

  const fetchWebhookInfo = () => {
    SpringApi.get(`${BASE}/webhook/info`)
      .then(res => {
        setWebhookUrl((res.data.data && res.data.data.webhookUrl) || "");
      })
      .catch(() => {});
  };

  const handleDelete = id => {
    if (!window.confirm("Delete this mapping?")) return;
    SpringApi.delete(`${BASE}/mapping/${id}`)
      .then(() => {
        if (toaster.current) toaster.current.success("Mapping deleted");
        fetchMappings();
      })
      .catch(err => {
        if (toaster.current) toaster.current.error(err.message);
      });
  };

  const handleFormChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    setFormLoading(true);
    SpringApi.post(`${BASE}/mapping`, { ...formData, tillId: parseInt(formData.tillId, 10) })
      .then(() => {
        if (toaster.current) toaster.current.success("Mapping saved!");
        setFormData(EMPTY_FORM);
        setShowForm(false);
        fetchMappings();
      })
      .catch(err => {
        if (toaster.current) toaster.current.error(err.message);
      })
      .finally(() => setFormLoading(false));
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    if (toaster.current) toaster.current.success("Webhook URL copied!");
  };

  const columns = [
    { Header: "ID", accessor: "id", className: "text-center", headerClassName: "react-table-header-class", filterable: false, width: 60 },
    { Header: "Epos Store ID", accessor: "eposStoreId", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Epos Terminal ID", accessor: "eposTerminalId", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Till ID", accessor: "tillId", className: "text-center", headerClassName: "react-table-header-class", filterable: false, width: 80 },
    {
      Header: "Status", accessor: "active", className: "text-center", headerClassName: "react-table-header-class", filterable: false, width: 90,
      Cell: row => (
        <span style={{ padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600",
          backgroundColor: row.value ? "#d4edda" : "#f8d7da", color: row.value ? "#155724" : "#721c24" }}>
          {row.value ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      Header: "Action", accessor: "id", id: "action", className: "text-center", headerClassName: "react-table-header-class", filterable: false, width: 100,
      Cell: row => (
        <div className="react-action-class">
          <Button className="c-btn c-danger mr-10" onClick={() => handleDelete(row.value)}>
            <div className="fs-14 medium-text"><i className="fas fa-trash mr-6" /> Delete</div>
          </Button>
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="plr-15">
        {webhookUrl && (
          <div className="roe-card-style mtb-15" style={{ backgroundColor: "#e8f4fd", borderLeft: "4px solid #3498db" }}>
            <div className="roe-card-body" style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
              <i className="fas fa-link" style={{ color: "#3498db", fontSize: 18 }} />
              <div style={{ flex: 1 }}>
                <div className="fs-14 demi-bold-text" style={{ marginBottom: 4 }}>Webhook URL — paste this into your Epos Now developer dashboard</div>
                <code style={{ fontSize: 13, wordBreak: "break-all" }}>{webhookUrl}</code>
              </div>
              <Button className="c-btn" onClick={copyWebhook} style={{ backgroundColor: "#3498db", color: "#fff", whiteSpace: "nowrap" }}>
                <i className="fas fa-copy mr-6" /> Copy
              </Button>
            </div>
          </div>
        )}

        <div className="roe-card-style mtb-15">
          <div className="roe-card-header module-header">
            <div className="flex-1 fs-16 demi-bold-text"><span className="hash"># </span> Terminal Mappings</div>
            <Button className="c-btn ma-5" onClick={() => setShowForm(!showForm)} style={{ backgroundColor: "#0f172a", color: "#fff" }}>
              <i className={`fas fa-${showForm ? "minus" : "plus"} mr-6`} /> {showForm ? "Cancel" : "Add Mapping"}
            </Button>
          </div>

          {showForm && (
            <div className="roe-card-body" style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 20 }}>
              <form onSubmit={handleFormSubmit}>
                <div className="row">
                  {[
                    { label: "GreenTill API Key", name: "apiKey", required: true },
                    { label: "Epos Store ID", name: "eposStoreId", required: true },
                    { label: "Epos Terminal ID", name: "eposTerminalId", required: true },
                    { label: "GreenTill Till ID", name: "tillId", required: true, type: "number" },
                    { label: "Epos Access Token", name: "eposAccessToken" },
                    { label: "Epos Refresh Token", name: "eposRefreshToken" }
                  ].map(f => (
                    <div className="col-md-4" key={f.name} style={{ marginBottom: 12 }}>
                      <label className="fs-14 medium-text">{f.label}{f.required && " *"}</label>
                      <input name={f.name} type={f.type || "text"} value={formData[f.name]} onChange={handleFormChange}
                        required={!!f.required} className="form-control react-form-input" placeholder={f.label} />
                    </div>
                  ))}
                </div>
                <Button type="submit" className="c-btn" disabled={formLoading}
                  style={{ backgroundColor: "#0f172a", color: "#fff", marginTop: 8 }}>
                  {formLoading ? <i className="fas fa-ellipsis-h" /> : <><i className="fas fa-save mr-6" /> Save Mapping</>}
                </Button>
              </form>
            </div>
          )}

          <div className="roe-card-body">
            <ReactTable style={{ border: "none", boxShadow: "none" }} data={tblData} columns={columns}
              defaultPageSize={10} minRows={2} className="-striped -highlight custom-react-table-theme-class"
              loading={loader} LoadingComponent={Loader} />
          </div>
          <Toaster ref={toaster} />
        </div>
      </div>
    </div>
  );
};

export default withRouter(EposNowTable);

